import React from 'react';
import PropTypes from 'prop-types';
import { viewports } from '@telus-uds/system-constants';
import { a11yProps, viewProps, getA11yPropsFromHtmlTag, layoutTags, selectSystemProps, BaseView, StyleSheet, createMediaQueryStyles, useResponsiveProp } from '../utils';
import Row from './Row';
import Col from './Col';
import GutterContext from './providers/GutterContext';
import applyInheritance from './helpers';
import { useTheme } from '../ThemeProvider';
import { useViewport } from '../ViewportProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const CONTENT_MAX_WIDTH = 'max';
const CONTENT_FULL_WIDTH = 'full';

/**
 * Resolves the maximum width for content based on the provided value and responsive width.
 *
 * @param {number|string|null|undefined} contentMinWidthValue - The minimum width value for the content.
 *        Can be a number, a special string constant (e.g., CONTENT_FULL_WIDTH, CONTENT_MAX_WIDTH), or null/undefined.
 * @param {number} responsiveWidth - The responsive width to use when contentMinWidthValue is CONTENT_MAX_WIDTH.
 * @returns {number|string|null} The resolved maximum width value, or null if full width is desired.
 */
const resolveContentMaxWidth = (contentMinWidthValue, responsiveWidth) => {
  if (!contentMinWidthValue || contentMinWidthValue === CONTENT_FULL_WIDTH) {
    return null;
  }
  if (Number.isFinite(contentMinWidthValue)) {
    return contentMinWidthValue;
  }
  if (contentMinWidthValue === CONTENT_MAX_WIDTH) {
    return responsiveWidth;
  }
  return contentMinWidthValue;
};

/**
 * Calculates the maximum width for a given viewport based on limitWidth and contentMinWidth settings.
 *
 * @param {string} viewportKey - The viewport key ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {boolean} limitWidth - Whether to limit the width to viewport breakpoints
 * @param {any} contentMinWidth - The contentMinWidth prop value
 * @param {number|string|null} maxWidth - The resolved max width value
 * @returns {number|string|null} The calculated maximum width for the viewport
 */
const getMaxWidthForViewport = (viewportKey, limitWidth, contentMinWidth, maxWidth) => {
  if (limitWidth) {
    if (viewportKey === 'xs') {
      return null;
    }
    return viewports.map.get(viewportKey);
  }
  if (contentMinWidth) {
    return maxWidth;
  }
  return null;
};

/**
 * A mobile-first flexbox grid.
 */

const FlexGrid = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    limitWidth = true,
    gutter = true,
    outsideGutter = true,
    xsReverse,
    smReverse,
    mdReverse,
    lgReverse,
    xlReverse,
    tag,
    accessibilityRole,
    children,
    dataSet,
    contentMinWidth,
    ...rest
  } = _ref;
  const reverseLevel = applyInheritance([xsReverse, smReverse, mdReverse, lgReverse, xlReverse]);
  const viewport = useViewport();
  const {
    themeOptions,
    themeOptions: {
      enableMediaQueryStyleSheet
    }
  } = useTheme();
  let flexgridStyles;
  let mediaIds;
  const contentMinWidthValue = useResponsiveProp(contentMinWidth);
  const responsiveWidth = useResponsiveProp(themeOptions?.contentMaxWidth);
  const maxWidth = resolveContentMaxWidth(contentMinWidthValue, responsiveWidth);
  const stylesByViewport = {
    xs: {
      maxWidth: getMaxWidthForViewport('xs', limitWidth, contentMinWidth, maxWidth),
      flexDirection: reverseLevel[0] ? 'column-reverse' : 'column'
    },
    sm: {
      maxWidth: getMaxWidthForViewport('sm', limitWidth, contentMinWidth, maxWidth),
      flexDirection: reverseLevel[1] ? 'column-reverse' : 'column'
    },
    md: {
      maxWidth: getMaxWidthForViewport('md', limitWidth, contentMinWidth, maxWidth),
      flexDirection: reverseLevel[2] ? 'column-reverse' : 'column'
    },
    lg: {
      maxWidth: getMaxWidthForViewport('lg', limitWidth, contentMinWidth, maxWidth),
      flexDirection: reverseLevel[3] ? 'column-reverse' : 'column'
    },
    xl: {
      maxWidth: getMaxWidthForViewport('xl', limitWidth, contentMinWidth, maxWidth),
      flexDirection: reverseLevel[4] ? 'column-reverse' : 'column'
    }
  };
  const staticStyles = {
    flexWrap: 'wrap',
    width: outsideGutter ? '100%' : 'auto',
    marginVertical: 0,
    marginHorizontal: outsideGutter ? 'auto' : -16
  };
  if (enableMediaQueryStyleSheet) {
    const mediaQueryStyles = createMediaQueryStyles(stylesByViewport);
    const {
      ids,
      styles
    } = StyleSheet.create({
      flexgrid: {
        ...staticStyles,
        ...mediaQueryStyles
      }
    });
    flexgridStyles = styles.flexgrid;
    mediaIds = ids.flexgrid;
  } else {
    flexgridStyles = {
      ...staticStyles,
      ...stylesByViewport[viewport]
    };
  }
  const props = {
    accessibilityRole,
    ...getA11yPropsFromHtmlTag(tag, accessibilityRole),
    ...selectProps(rest)
  };
  const dataSetValue = mediaIds ? {
    media: mediaIds,
    ...dataSet
  } : dataSet;
  return /*#__PURE__*/_jsx(GutterContext.Provider, {
    value: gutter,
    children: /*#__PURE__*/_jsx(BaseView, {
      ref: ref,
      ...props,
      style: flexgridStyles,
      dataSet: dataSetValue,
      children: children
    })
  });
});
FlexGrid.displayName = 'FlexGrid';
FlexGrid.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Whether or not to give the grid a fixed width. This also centres the grid horizontally.
   */
  limitWidth: PropTypes.bool,
  /**
   * Whether or not to include gutters in between columns.
   */
  gutter: PropTypes.bool,
  /**
   * Whether or not to include gutter at the ends to the left and right of the FlexGrid
   */
  outsideGutter: PropTypes.bool,
  /**
   * Choose if the item order should be reversed from the 'xs' breakpoint. When you pass in false, the order will be normal from the xs breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  xsReverse: PropTypes.bool,
  /**
   * Choose if the item order should be reversed from the 'sm' breakpoint. When you pass in false, the order will be normal from the sm breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  smReverse: PropTypes.bool,
  /**
   * Choose if the item order should be reversed from the 'md' breakpoint. When you pass in false, the order will be normal from the md breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  mdReverse: PropTypes.bool,
  /**
   * Choose if the item order should be reversed from the 'lg' breakpoint. When you pass in false, the order will be normal from the lg breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  lgReverse: PropTypes.bool,
  /**
   * Choose if the item order should be reversed from the 'xl' breakpoint. When you pass in false, the order will be normal from the xl breakpoint. By default, it inherits the behaviour set by the preceding prop.
   */
  xlReverse: PropTypes.bool,
  /**
   * Optional semantic HTML tag, to apply to the container on web. Does not change styling.
   *
   * In native apps, if a header tag is provided ("h1", "h2" etc) and accessibilityRole prop
   * is not defined, the accessibilityRole will default to "heading".
   */
  tag: PropTypes.oneOf(layoutTags),
  /**
   * The rows and columns of the Grid. Will typically be `FlexGrid.Row` and `FlexGrid.Col` components.
   */
  children: PropTypes.node.isRequired,
  /**
   * The minimum width of the content in the FlexGrid.
   * This prop accepts responsive values for different viewports. If a number is provided,
   * it will be the max content width for the desired viewport.
   * - `xs`: 'max' | 'full' | <number>
   * - `sm`: 'max' | 'full' | <number>
   * - `md`: 'max' | 'full' | <number>
   * - `lg`: 'max' | 'full' | <number>
   * - `xl`: 'max' | 'full' | <number>
   */
  contentMinWidth: PropTypes.shape({
    xl: PropTypes.oneOfType([PropTypes.oneOf(['max', 'full']), PropTypes.number]),
    lg: PropTypes.oneOfType([PropTypes.oneOf(['max', 'full']), PropTypes.number]),
    md: PropTypes.oneOfType([PropTypes.oneOf(['max', 'full']), PropTypes.number]),
    sm: PropTypes.oneOfType([PropTypes.oneOf(['max', 'full']), PropTypes.number]),
    xs: PropTypes.oneOfType([PropTypes.oneOf(['max', 'full']), PropTypes.number])
  })
};
FlexGrid.Row = Row;
FlexGrid.Col = Col;
export default FlexGrid;